import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Alert, Button, DatePicker, Input, Modal, Select, Tooltip, message } from 'antd-v5';
import dayjs from 'dayjs';
import moment from 'moment';
import { getVendorItems, createInquiry, updateInquiry, getInquiriesByClient } from '../../../api/vendors';
import { getMerchantTags } from '../../../services/clients';
import { getRequest } from '../../../core/apiRequests';
import { SearchClientsInputV2 } from '../../CustomizedAntDesign/SearchClientsInputV2';
import { OPEN_STATUSES } from '../../../components/Routes/Rolex/utils';
import './styles.scss';

const { TextArea } = Input;

const STATUS_OPTIONS = [
  { label: 'Waiting', value: 'WAITING' },
  { label: 'Ordered', value: 'ORDERED' },
  { label: 'Canceled', value: 'CANCELED' },
  { label: 'Fulfilled', value: 'FULFILLED' },
];

const AddRolexInquiryModal = ({ open, onCancel, onSave, initialModels, initialClient, vendorId, auth, inquiry }) => {
  const history = useHistory();
  const isEditMode = !!inquiry;
  const [client, setClient] = useState(null);
  const [models, setModels] = useState([]);
  const [story, setStory] = useState('');
  const [tags, setTags] = useState([]);
  const [associate, setAssociate] = useState(null);
  const [inquiryDate, setInquiryDate] = useState(null);
  const [status, setStatus] = useState('WAITING');
  const [modelOptions, setModelOptions] = useState([]);
  const [modelSearchLoading, setModelSearchLoading] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  const [associateOptions, setAssociateOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [existingOpenInquiry, setExistingOpenInquiry] = useState(null);
  const [checkingOpenInquiry, setCheckingOpenInquiry] = useState(false);

  const isManagerOrAdmin = useMemo(() => ['ADMIN', 'MANAGER'].includes(auth?.role), [auth?.role]);

  const currentUserName = useMemo(() => {
    return [auth?.firstName, auth?.lastName].filter(Boolean).join(' ') || '';
  }, [auth?.firstName, auth?.lastName]);

  useEffect(() => {
    if (open) {
      if (isEditMode) {
        setClient(inquiry.clientId ? { id: inquiry.clientId, name: inquiry.clientName } : null);
        setModels(inquiry.requestedProducts?.map(p => p.mpn) || inquiry.models || []);
        setStory(inquiry.story || '');
        setTags(
          (inquiry.tags || []).map(t => (typeof t === 'object' ? t.tagName || t.name : t)).filter(Boolean),
        );
        setStatus(inquiry.status || 'WAITING');
        setAssociate(
          inquiry.associateId
            ? { id: inquiry.associateId.toString(), name: inquiry.associateName || inquiry.associate }
            : null,
        );
        // Parse inquiryDate defensively. Accepts either raw API YYYYMMDD or
        // the transformed MM/DD/YYYY display string.
        let parsedDate = dayjs();
        if (inquiry.inquiryDate) {
          const str = String(inquiry.inquiryDate);
          const m = /^\d{8}$/.test(str)
            ? moment(str, 'YYYYMMDD')
            : /^\d{2}\/\d{2}\/\d{4}$/.test(str)
              ? moment(str, 'MM/DD/YYYY')
              : moment(str);
          if (m.isValid()) parsedDate = dayjs(m.valueOf());
        }
        setInquiryDate(parsedDate);
      } else {
        setModels(initialModels?.length > 0 ? initialModels : []);
        setInquiryDate(dayjs());
        setAssociate(auth?.userId ? { id: auth.userId, name: currentUserName } : null);
        if (initialClient?.id) setClient({ id: initialClient.id, name: initialClient.name || '' });
      }
    }
  }, [open, initialModels, initialClient, auth?.userId, currentUserName, isEditMode, inquiry]);

  useEffect(() => {
    if (!open) {
      setClient(null);
      setModels([]);
      setStory('');
      setTags([]);
      setAssociate(null);
      setInquiryDate(null);
      setStatus('WAITING');
      setModelOptions([]);
      setExistingOpenInquiry(null);
      setCheckingOpenInquiry(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || isEditMode || !client?.id) {
      setExistingOpenInquiry(null);
      return;
    }
    let cancelled = false;
    setCheckingOpenInquiry(true);
    getInquiriesByClient(client.id)
      .then(response => {
        if (cancelled) return;
        const inquiries = Array.isArray(response?.data) ? response.data : [];
        const openInquiry = inquiries.find(i => OPEN_STATUSES.includes(i.status));
        setExistingOpenInquiry(openInquiry || null);
      })
      .catch(() => {
        if (!cancelled) setExistingOpenInquiry(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingOpenInquiry(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isEditMode, client?.id]);

  useEffect(() => {
    if (open) {
      getMerchantTags({ query: { limit: 500 }, loadingStatusOff: true })
        .then(response => {
          const options = (response?.results || []).map(tag => ({
            label: tag.name,
            value: tag.name,
          }));
          setTagOptions(options);
        })
        .catch(() => setTagOptions([]));
    }
  }, [open]);

  useEffect(() => {
    if (open && isManagerOrAdmin && auth?.merchantId) {
      const fetchAssociates = async () => {
        const data = await getRequest({
          params: ['associates', auth.merchantId],
          query: { singleStore: 0 },
          loadingStatusOff: true,
        });
        if (Array.isArray(data) && data.length > 0) {
          setAssociateOptions(
            data.map(a => ({
              value: a.id?.toString(),
              label: a.fullName || `${a.first_name || ''} ${a.last_name || ''}`.trim(),
            })),
          );
        }
      };
      fetchAssociates();
    }
  }, [open, isManagerOrAdmin, auth?.merchantId]);

  const handleModelSearch = useCallback(
    async searchValue => {
      if (!searchValue || searchValue.length < 2 || !vendorId) return;
      setModelSearchLoading(true);
      try {
        const response = await getVendorItems(vendorId, {
          params: { search: searchValue, limit: 20, offset: 0 },
        });
        const items = response?.data?.data || response?.data || [];
        const options = items.map(item => ({
          label: (
            <div className="add-rolex-inquiry-modal__model-option">
              {item.imageUrl && (
                <img className="add-rolex-inquiry-modal__model-image" src={item.imageUrl} alt={item.name} />
              )}
              <div className="add-rolex-inquiry-modal__model-info">
                <span className="add-rolex-inquiry-modal__model-name">{item.name}</span>
                <span className="add-rolex-inquiry-modal__model-mpn">{item.mpn}</span>
              </div>
            </div>
          ),
          value: item.mpn,
        }));
        setModelOptions(options);
      } catch {
        setModelOptions([]);
      } finally {
        setModelSearchLoading(false);
      }
    },
    [vendorId],
  );

  const isTerminal = isEditMode && ['CANCELED', 'FULFILLED'].includes(inquiry?.status);
  const terminalLabel = inquiry?.status === 'FULFILLED' ? 'fulfilled' : 'canceled';

  const canAssociateCancel =
    isEditMode && !isManagerOrAdmin && inquiry?.status === 'WAITING' && !isTerminal;

  const handleCancelInquiry = useCallback(() => {
    if (!inquiry?.id) return;
    Modal.confirm({
      title: 'Cancel this Rolex inquiry?',
      content:
        'This will set the status to Canceled. The inquiry will remain on record but will no longer be active.',
      okText: 'Yes, cancel inquiry',
      cancelText: 'Keep inquiry',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await updateInquiry(inquiry.id, { status: 'CANCELED' });
          message.success('Inquiry canceled');
          if (onSave) onSave();
          onCancel();
        } catch (err) {
          message.error(err?.response?.data?.message || 'Failed to cancel inquiry');
        }
      },
    });
  }, [inquiry, onSave, onCancel]);

  const handleSave = useCallback(async () => {
    if (!client) {
      message.error('Client is required');
      return;
    }
    if (models.length === 0) {
      message.error('At least one model is required');
      return;
    }
    if (isManagerOrAdmin && !associate) {
      message.error('Associate is required');
      return;
    }
    if (isManagerOrAdmin && !inquiryDate) {
      message.error('Inquiry date is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        clientId: client.id,
        models,
        story: story || undefined,
        tags: tags.length > 0 ? tags : undefined,
      };

      if (isManagerOrAdmin) {
        if (associate) payload.associateId = parseInt(associate.id, 10);
        if (inquiryDate) payload.inquiryDate = inquiryDate.format('YYYYMMDD');
        if (status) payload.status = status;
      }

      if (isEditMode) {
        await updateInquiry(inquiry.id, payload);
      } else {
        await createInquiry(payload);
      }

      message.success(isEditMode ? 'Inquiry updated' : 'Inquiry created');
      if (onSave) onSave();
      onCancel();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to save inquiry');
    } finally {
      setSaving(false);
    }
  }, [
    client,
    models,
    story,
    tags,
    associate,
    inquiryDate,
    status,
    isManagerOrAdmin,
    isEditMode,
    inquiry,
    onSave,
    onCancel,
  ]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
      destroyOnClose
      width={520}
      className="add-rolex-inquiry-modal"
    >
      <div className="add-rolex-inquiry-modal__header">
        <h2 className="add-rolex-inquiry-modal__title">{isEditMode ? 'Edit Rolex Inquiry' : 'Add a Rolex Inquiry'}</h2>
        {canAssociateCancel && (
          <Button type="text" danger onClick={handleCancelInquiry}>
            Cancel Inquiry
          </Button>
        )}
        {isTerminal ? (
          <Tooltip title={`This inquiry is ${terminalLabel} and cannot be edited.`}>
            <Button type="primary" style={{ borderRadius: 8 }} disabled>
              Save
            </Button>
          </Tooltip>
        ) : existingOpenInquiry ? (
          <Tooltip title="This client already has an open inquiry.">
            <Button type="primary" style={{ borderRadius: 8 }} disabled>
              Save
            </Button>
          </Tooltip>
        ) : (
          <Button
            type="primary"
            style={{ borderRadius: 8 }}
            onClick={handleSave}
            loading={saving}
            disabled={checkingOpenInquiry}
          >
            Save
          </Button>
        )}
        <Button onClick={onCancel}>Close</Button>
      </div>

      {isTerminal && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message={`This inquiry is ${terminalLabel} and can no longer be edited.`}
        />
      )}

      {existingOpenInquiry && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message="This client already has an open inquiry."
          description={
            <Button
              type="link"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                onCancel();
                history.push(`/Clients?client=${client.id}&clientTab=rolex`);
              }}
            >
              View existing inquiry
            </Button>
          }
        />
      )}

      <div className="add-rolex-inquiry-modal__form" style={{ marginTop: 24 }}>
        <div className="add-rolex-inquiry-modal__field">
          <div className="add-rolex-inquiry-modal__label">
            Client <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
          </div>
          {isEditMode || initialClient?.id ? (
            <Input value={client?.name || ''} disabled />
          ) : (
            <SearchClientsInputV2 placeholder="Find a client" onSelect={setClient} />
          )}
        </div>

        <div className="add-rolex-inquiry-modal__field">
          <div className="add-rolex-inquiry-modal__label">
            {isManagerOrAdmin ? 'Rolex Model' : 'Models'}{' '}
            <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
          </div>
          <Select
            mode="multiple"
            placeholder="Search for a model"
            value={models}
            onChange={setModels}
            onSearch={handleModelSearch}
            loading={modelSearchLoading}
            options={modelOptions}
            optionLabelProp="value"
            filterOption={false}
            showSearch
            disabled={isTerminal}
            notFoundContent={modelSearchLoading ? 'Searching...' : 'Type to search models'}
          />
        </div>

        <div className="add-rolex-inquiry-modal__field">
          <div className="add-rolex-inquiry-modal__label">
            Story
          </div>
          <TextArea
            rows={4}
            placeholder="Textarea"
            value={story}
            onChange={e => setStory(e.target.value)}
            disabled={isTerminal}
          />
        </div>

        <div className="add-rolex-inquiry-modal__field">
          <div className="add-rolex-inquiry-modal__label">
            Tags
          </div>
          <Select
            mode="multiple"
            placeholder="Add tags"
            value={tags}
            onChange={setTags}
            options={tagOptions}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            showSearch
            disabled={isTerminal}
          />
        </div>

        {isManagerOrAdmin && (
          <>
            <div className="add-rolex-inquiry-modal__divider">MANAGERS AND ADMINS</div>

            <div className="add-rolex-inquiry-modal__field">
              <div className="add-rolex-inquiry-modal__label">
                Associate <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
              </div>
              <Select
                placeholder="Find a Sales Associate"
                value={associate?.id?.toString() || undefined}
                onChange={(val, option) => setAssociate({ id: val, name: option?.label })}
                options={associateOptions}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                showSearch
                disabled={isTerminal}
                style={{ width: '100%' }}
              />
            </div>

            <div className="add-rolex-inquiry-modal__field">
              <div className="add-rolex-inquiry-modal__label">
                Inquiry Date <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
              </div>
              <DatePicker
                placeholder="Select date"
                value={inquiryDate}
                onChange={setInquiryDate}
                disabled={isTerminal}
                disabledDate={current => current && current.isAfter(dayjs().endOf('day'))}
                style={{ width: '100%' }}
              />
            </div>

            <div className="add-rolex-inquiry-modal__field">
              <div className="add-rolex-inquiry-modal__label">
                Status <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
              </div>
              <Select
                value={status}
                onChange={setStatus}
                options={STATUS_OPTIONS}
                disabled={isTerminal}
                style={{ width: '100%' }}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AddRolexInquiryModal);
